/**
 * PracticeSwiper.tsx
 *
 * A lightweight swiper for the HOME left-panel that:
 *  - Shows ONLY the practice slides (Adhama → Nadi → Chandra → Bhramari → Meditation → Yoga Nidra)
 *  - Has a slim header showing just the current slide title
 *  - Has NO "All / Practice" toggle — practice only
 *  - Manual navigation only — no autoplay/slideshow
 *  - Calls onComplete() when audio finishes on the last slide → switches to SimpleView
 */

import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

import HTLDSlide8Body  from "./8_adhamaPranayama_body";
import HTLDSlide9Body  from "./9_nadiShuddhi_body";
import HTLDSlide10Body from "./10_chandraBhedana_body";
import { BhramariBody } from "./10a_bhramari";
import { accent10a } from "./slide10a.data";
import HTLDSlide11Body from "./11_yogaNidra_body";
import MeditationBody from "./10b_meditation_body";
import { FONT_BODY, FONT_MONO } from "theme/fonts";

/* ─── constants ──────────────────────────────────────────────────────────── */
const ACCENT = "#378add";

// `title` is shown in the header; the shorter `label` is used for the bottom
// tabs (same names as the tutorial's label bar) so all of them fit.
const PRACTICE_SLIDES = [
  { title: "Adhama Pranayama", label: "Adhama",          accentTitle: "Pranayama", accent: "#e07b39", component: <HTLDSlide8Body  /> },
  { title: "Nadi Shuddhi",     label: "Nadi Shuddhi",    accentTitle: "Shuddhi",   accent: "#3ecb8a", component: <HTLDSlide9Body  /> },
  { title: "Chandra Bhedana",  label: "Chandra Bhedana", accentTitle: "Bhedana",   accent: "#7b8ef0", component: <HTLDSlide10Body /> },
  { title: "Bhramari",         label: "Bhramari",        accentTitle: "Bhramari",  accent: accent10a, component: <BhramariBody /> },
  { title: "Meditation",       label: "Meditation",      accentTitle: "Meditation", accent: "#4bb8b0", component: <MeditationBody /> },
  { title: "Yoga Nidra",       label: "Yoga Nidra",      accentTitle: "Nidra",     accent: "#c090e0", component: <HTLDSlide11Body /> },
];

const TAB_FADE =
  "linear-gradient(to right, transparent 0, black 10px, black calc(100% - 16px), transparent 100%)";

const LAST_IDX = PRACTICE_SLIDES.length - 1;

/* ─── inject swiper height fix once ─────────────────────────────────────── */
const STYLE_ID = "practice-swiper-styles";
if (!document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    .prs-bar::-webkit-scrollbar { display: none; }
    .prs-bar { scrollbar-width: none; -ms-overflow-style: none; }
    .prs-swiper .swiper,
    .prs-swiper .swiper-wrapper { height: 100% !important; }
    .prs-swiper .swiper-slide  { height: 100% !important; overflow: hidden; }
  `;
  document.head.appendChild(s);
}

type Props = {
  onSkip?: () => void;
  onComplete?: () => void;
  isAudioPlaying?: boolean;
};

/* ═══════════════════════════════════════════════════════════════════════════
   PracticeSwiper
   ═══════════════════════════════════════════════════════════════════════════ */
const PracticeSwiper: React.FC<Props> = ({ onSkip, onComplete, isAudioPlaying }) => {
  const swiperRef      = useRef<SwiperType | null>(null);
  const activeTitleRef = useRef<HTMLButtonElement>(null);

  const [active, setActive] = useState(0);

  const currentSlide = PRACTICE_SLIDES[active];
  const isLastSlide  = active === LAST_IDX;

  /* ── scroll active tab into view ── */
  useEffect(() => {
    activeTitleRef.current?.scrollIntoView({
      behavior: "smooth", block: "nearest", inline: "center",
    });
  }, [active]);

  /* ── stop breathing guides when slide changes ── */
  const stopGuides = () => window.dispatchEvent(new Event("yogyism:stop"));

  /* ── NAVIGATE TO SLIDE ── */
  const goTo = (i: number) => {
    const sw = swiperRef.current;
    if (!sw) return;
    sw.slideTo(i);
  };

  /* ── Prev button handler ── */
  const handlePrev = () => {
    const sw = swiperRef.current;
    if (!sw || active === 0) return;
    sw.slidePrev();
  };

  /* ── Next button handler ── */
  const handleNext = () => {
    const sw = swiperRef.current;
    if (!sw) return;

    if (active === LAST_IDX) {
      onSkip?.();
      return;
    }

    sw.slideNext();
  };


  /* ── Auto-advance when a practice's audio finishes (dispatched as yogyism:next) ──
     On the last slide (Yoga Nidra), completion switches to SimpleView instead. */
useEffect(() => {
  const handleNextEvent = () => {
    const sw = swiperRef.current;
    if (sw && !sw.isEnd) {
      // Not the last practice → advance to the next one.
      sw.slideNext();
    } else {
      // Last practice finished → move on to the Session view.
      setTimeout(() => { onComplete ? onComplete() : onSkip?.(); }, 800);
    }
  };
  window.addEventListener("yogyism:next", handleNextEvent);
  return () => window.removeEventListener("yogyism:next", handleNextEvent);
}, [onComplete, onSkip]);
  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div style={{
      position: "relative",
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      background: "transparent",
      overflow: "hidden",
    }}>

      {/* ── Slim title header ─────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0,
        background: "#071520",
        borderBottom: "1px solid rgba(55,138,221,0.14)",
        padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10,
      }}>
        <div style={{
          fontFamily: FONT_BODY,
          fontSize: "clamp(12px,1.1vw,16px)",
          fontStyle: "normal", fontWeight: 700,
          color: "#e8f4ff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {currentSlide.title.replace(currentSlide.accentTitle, "").trim()}{" "}
          <span style={{ color: currentSlide.accent }}>{currentSlide.accentTitle}</span>
        </div>

        <div style={{
          fontFamily: FONT_MONO,
          fontSize: 11, color: "rgba(100,150,200,0.55)",
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {active + 1} / {PRACTICE_SLIDES.length}
        </div>
      </div>

      {/* ── Swiper ─────────────────────────────────────────────────────────── */}
      <div className="prs-swiper" style={{ flex: 1, minHeight: 0, overflow: "hidden", background: "transparent" }}>
        <Swiper
          modules={[Navigation, Keyboard]}
          slidesPerView={1}
          spaceBetween={0}
          loop={false}
          keyboard={{ enabled: true }}
          onSwiper={(s: any) => { swiperRef.current = s; }}
          onSlideChange={(s: any) => {
            setActive(s.activeIndex);
            stopGuides();
          }}
          style={{ width: "100%", height: "100%" }}
        >
          {PRACTICE_SLIDES.map((slide, i) => (
            <SwiperSlide key={i} style={{ height: "100%", overflow: "hidden" }}>
              {slide.component}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* ── Control bar ────────────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0,
        background: "#071520",
        borderTop: "1px solid rgba(55,138,221,0.12)",
        padding: "6px 0 0",
        pointerEvents: isAudioPlaying ? "none" : "auto",
        opacity: isAudioPlaying ? 0.4 : 1,
        transition: "opacity 0.3s ease",
      }}>
        <div style={{ height: 40, display: "flex", alignItems: "center" }}>
        {/* Title strip scrolls on its own so it can never slide under the
            buttons; the edge fade hints at more titles when it overflows. */}
        <div
          className="prs-bar"
          style={{
            flex: 1, minWidth: 0, height: "100%",
            display: "flex", alignItems: "center",
            paddingLeft: 10, paddingRight: 14, overflowX: "auto",
            WebkitMaskImage: TAB_FADE, maskImage: TAB_FADE,
          }}
        >
          {/* Slide title tabs */}
          {PRACTICE_SLIDES.map((slide, i) => {
            const isActive = i === active;
            const isPast   = i < active;
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span style={{
                    flexShrink: 0, color: "rgba(55,138,221,0.28)",
                    fontSize: 12, margin: "0 6px", userSelect: "none",
                  }}>·</span>
                )}
                <button
                  ref={isActive ? activeTitleRef : null}
                  onClick={() => goTo(i)}
                  style={{
                    flexShrink: 0, background: "none", border: "none", padding: 0,
                    cursor: "pointer",
                    fontFamily: FONT_BODY,
                    fontSize: "11px", fontStyle: "normal",
                    fontWeight: isActive ? 700 : 300,
                    color: isActive
                      ? slide.accent
                      : isPast
                      ? "rgba(130,175,220,0.55)"
                      : "rgba(100,150,200,0.40)",
                    transition: "color 0.2s", whiteSpace: "nowrap",
                    textShadow: isActive ? `0 0 8px ${slide.accent}88` : "none",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.color = "rgba(160,200,240,0.85)";
                  }}
                  onMouseLeave={e => {
                    if (!isActive)
                      e.currentTarget.style.color = isPast
                        ? "rgba(130,175,220,0.55)"
                        : "rgba(100,150,200,0.40)";
                  }}
                >
                  {slide.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>

          {/* Skip / Prev / Next */}
          <div style={{
            flexShrink: 0, display: "flex", alignItems: "center",
            gap: 8, paddingLeft: 6, paddingRight: 10,
          }}>
            <button
              onClick={() => onSkip?.()}
              style={{
                background: "none",
                border: `1px solid rgba(55,138,221,0.22)`,
                borderRadius: 8,
                padding: "4px 9px",
                cursor: "pointer",
                fontFamily: FONT_BODY,
                fontSize: 11,
                fontWeight: 500,
                color: ACCENT,
                transition: "all 0.18s",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              Skip
              <svg width="10" height="8" viewBox="0 0 12 10" fill={ACCENT}>
                <polygon points="0,0 6,5 0,10"/>
                <polygon points="6,0 12,5 6,10"/>
              </svg>
            </button>

            <button
              onClick={handlePrev}
              disabled={active === 0}
              style={{
                background: "none",
                border: `1px solid rgba(55,138,221,${active === 0 ? 0.1 : 0.22})`,
                borderRadius: 8, padding: "4px 9px", cursor: active === 0 ? "default" : "pointer",
                fontFamily: FONT_BODY, fontSize: 11, fontWeight: 500,
                color: active === 0 ? "rgba(100,150,200,0.20)" : ACCENT,
                transition: "all 0.18s", display: "flex", alignItems: "center", gap: 4,
              }}
            >
              <svg width="8" height="8" viewBox="0 0 10 10"
                fill={active === 0 ? "rgba(100,150,200,0.20)" : ACCENT}>
                <polygon points="10,0 0,5 10,10"/>
              </svg>
              Prev
            </button>

            <button
              onClick={handleNext}
              style={{
                background: "none",
                border: `1px solid rgba(55,138,221,${active === LAST_IDX ? 0.1 : 0.22})`,
                borderRadius: 8, padding: "4px 9px",
                cursor: "pointer",
                fontFamily: FONT_BODY, fontSize: 11, fontWeight: 500,
                color: active === LAST_IDX ? "rgba(100,150,200,0.20)" : ACCENT,
                transition: "all 0.18s", display: "flex", alignItems: "center", gap: 4,
              }}
            >
              Next
              <svg width="8" height="8" viewBox="0 0 10 10"
                fill={active === LAST_IDX ? "rgba(100,150,200,0.20)" : ACCENT}>
                <polygon points="0,0 10,5 0,10"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Thin bottom border line (no progress bar) */}
        <div style={{ height: 3, background: "rgba(55,138,221,0.10)" }} />
      </div>
    </div>
  );
};

export default PracticeSwiper;